import RestService from './RestService';

class InsightsService extends RestService {
  constructor() {
    super('ai-insights');
  }

  /**
   * Get list of system prompts (without content)
   * @returns {Promise<Array>} List of system prompts
   */
  async getSystemPromptsList() {
    try {
      const url = this.baseUrl + '/v3/api/ai-insights/system-prompts';
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching system prompts list:', error);
      throw error;
    }
  }

  /**
   * Get full system prompt content by ID
   * @param {number} id - The prompt ID
   * @returns {Promise<Object>} Full system prompt with content
   */
  async getSystemPromptContent(id) {
    try {
      const url = this.baseUrl + `/v3/api/ai-insights/system-prompts/${id}`;
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching system prompt content:', error);
      throw error;
    }
  }

  /**
   * Get list of suggested prompts (without content)
   * @returns {Promise<Array>} List of suggested prompts
   */
  async getSuggestedPromptsList() {
    try {
      const url = this.baseUrl + '/v3/api/ai-insights/suggested-prompts';
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching suggested prompts list:', error);
      throw error;
    }
  }

  /**
   * Get full suggested prompt content by ID
   * @param {number} id - The prompt ID
   * @returns {Promise<Object>} Full suggested prompt with content
   */
  async getSuggestedPromptContent(id) {
    try {
      const url = this.baseUrl + `/v3/api/ai-insights/suggested-prompts/${id}`;
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching suggested prompt content:', error);
      throw error;
    }
  }

  /**
   * Get admin conversations list
   * @returns {Promise<Array>} List of admin conversations
   */
  async getAdminConversations() {
    try {
      const url = this.baseUrl + '/v3/api/ai-insights/admin-conversations';
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching admin conversations:', error);
      throw error;
    }
  }

  /**
   * Get Smart Search 2.0 admin sessions list (athena IntelSession)
   * @returns {Promise<Array>} List of admin sessions
   */
  async getAdminSessions() {
    try {
      const url = this.baseUrl + '/v3/api/ai-insights/admin-sessions';
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching admin sessions:', error);
      throw error;
    }
  }

  /**
   * Get Smart Search 2.0 session details by ID (admin)
   * @param {string} sessionId - The athena session UUID
   * @returns {Promise<Object>} Full session details (turns + tool calls)
   */
  async getAdminSessionDetails(sessionId) {
    try {
      const url = this.baseUrl + `/v3/api/ai-insights/admin-sessions/${sessionId}`;
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching admin session details:', error);
      throw error;
    }
  }

  /**
   * Export Smart Search 2.0 admin session turns as CSV for one customer.
   * @param {Object} params
   * @param {number} params.customerId
   * @param {string} [params.startDate] - YYYY-MM-DD
   * @param {string} [params.endDate] - YYYY-MM-DD
   * @param {number[]} [params.userIds]
   * @param {boolean} [params.includeInternalUsers]
   * @param {'SMART_SEARCH'|'MANUAL_FILTERS'} [params.initiationType]
   * @param {number} [params.minUserTurns]
   * @returns {Promise<{ csv: string, turnCount: number, sessionCount: number, filename: string }>}
   */
  async exportAdminSessions(params) {
    const query = new URLSearchParams();
    query.set('customerId', String(params.customerId));

    if (params.startDate) {
      query.set('startDate', params.startDate);
    }
    if (params.endDate) {
      query.set('endDate', params.endDate);
    }
    if (Array.isArray(params.userIds) && params.userIds.length) {
      params.userIds.forEach((userId) => query.append('userIds', String(userId)));
    }
    if (params.includeInternalUsers === true) {
      query.set('includeInternalUsers', 'true');
    }
    if (params.initiationType) {
      query.set('initiationType', params.initiationType);
    }
    if (params.minUserTurns && params.minUserTurns > 1) {
      query.set('minUserTurns', String(params.minUserTurns));
    }

    const url = `${this.baseUrl}/v3/api/ai-insights/admin-sessions/export?${query.toString()}`;
    const response = await this.executeGet(url);
    return response.data || response;
  }

  /**
   * Fetch a full Smart Search 2.0 tool payload for an admin download.
   * @param {string} sessionId - The athena session UUID
   * @param {string} toolCallId - The persisted tool-call UUID
   * @returns {Promise<unknown>} Full tool result payload
   */
  async getAdminSessionToolResult(sessionId, toolCallId) {
    try {
      const url =
        this.baseUrl + `/v3/api/intelligence/tool-calls/admin/${sessionId}/${toolCallId}/result`;
      return await this.executeGet(url);
    } catch (error) {
      console.error('Error fetching admin tool-call result:', error);
      throw error;
    }
  }

  /**
   * Get conversation details by ID
   * @param {string} conversationId - The conversation ID
   * @returns {Promise<Object>} Full conversation details
   */
  async getConversationDetails(conversationId) {
    try {
      const url = this.baseUrl + `/v3/api/ai-insights/admin-conversations/${conversationId}`;
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching conversation details:', error);
      throw error;
    }
  }

  /**
   * Send a chat message to AI
   * @param {string} prompt - The user's prompt
   * @param {Object} context - Context object (e.g., { includeSnowflake: true })
   * @returns {Promise<string>} AI response
   */
  async sendAtlasChatMessage(prompt, context = { includeSnowflake: true }, conversationId = undefined) {
    try {
      const url = this.baseUrl + '/v3/api/bhat/chat';
      
      const payload = {
        prompt,
        context,
        ...(conversationId !== undefined && conversationId !== null && conversationId !== '' ? { conversationId } : {}),
      };
      const response = await this.post(url, payload);
      
      if (response.data && response.data.role && response.data.content) {
        return response.data;
      }
      
      return response.data || response;
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw error;
    }
  }

    /**
   * Send a chat message to AI for conversation diagnostics
   * @param {string} systemPrompt - System prompt for context
   * @param {number[]} contextIds - Array of context IDs
   * @param {Array} messageHistory - Array of previous messages
   * @returns {Promise<string>} AI response
   */
    async sendChatMessage(systemPrompt, contextIds, messageHistory) {
      try {
        const url = this.baseUrl + '/v3/api/ai-insights/admin-conversations/diagnostics';
        
        const payload = {
          contextIds,
          messageHistory,
          ...(systemPrompt && { systemPrompt }) // Only include if systemPrompt is provided
        };
        const response = await this.post(url, payload);
        
        if (response.data && response.data.role && response.data.content) {
          return response.data;
        }
        
        return response.data || response;
      } catch (error) {
        console.error('Error sending chat message:', error);
        throw error;
      }
    }

  /**
   * Get analytics data for admin conversations
   * @param {Object} filters - Filter parameters (customerId, userId, startDate, endDate, includeInternalUsers)
   * @returns {Promise<Object>} Analytics data
   */
  async getAdminConversationsAnalytics(filters = {includeInternalUsers: false}) {
    try {
      let url = this.baseUrl + '/v3/api/ai-insights/admin-conversations/analytics';
      const queryParams = [];
      queryParams.push(`includeInternalUsers=${filters.includeInternalUsers}`);
      
      if (filters.customerId) {
        queryParams.push(`customerId=${parseInt(filters.customerId)}`);
      }
      if (filters.userId) {
        queryParams.push(`userId=${parseInt(filters.userId)}`);
      }
      if (filters.startDate) {
        queryParams.push(`startDate=${encodeURIComponent(filters.startDate)}`);
      }
      if (filters.endDate) {
        queryParams.push(`endDate=${encodeURIComponent(filters.endDate)}`);
      }
      if (queryParams.length > 0) {
        url += '?' + queryParams.join('&');
      }
      
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching admin conversations analytics:', error);
      throw error;
    }
  }

  /**
   * Get Smart Search 2.0 analytics (same shape as getAdminConversationsAnalytics)
   * @param {Object} filters - Filter parameters (customerId, userId, startDate, endDate, includeInternalUsers)
   * @returns {Promise<Object>} Analytics data
   */
  async getAdminConversationsAnalyticsV2(filters = {includeInternalUsers: false}) {
    try {
      let url = this.baseUrl + '/v3/api/ai-insights/admin-conversations/analytics/v2';
      const queryParams = [];
      queryParams.push(`includeInternalUsers=${filters.includeInternalUsers}`);

      if (filters.customerId) {
        queryParams.push(`customerId=${parseInt(filters.customerId)}`);
      }
      if (filters.userId) {
        queryParams.push(`userId=${parseInt(filters.userId)}`);
      }
      if (filters.startDate) {
        queryParams.push(`startDate=${encodeURIComponent(filters.startDate)}`);
      }
      if (filters.endDate) {
        queryParams.push(`endDate=${encodeURIComponent(filters.endDate)}`);
      }
      if (queryParams.length > 0) {
        url += '?' + queryParams.join('&');
      }

      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching admin conversations analytics v2:', error);
      throw error;
    }
  }

  /**
   * Stream a chat completion response from the server.
   * Expects the server to return a chunked text stream (plain text or NDJSON/SSE-style lines).
   * onChunk will be called with each text chunk as it arrives.
   * Returns an object with an abort() function to cancel the request.
   *
   * @param {string} prompt - The user's prompt
   * @param {Object} context - Context object (e.g., { includeSnowflake: true })
   * @param {(text: string) => void} onChunk
   * @returns {{ abort: () => void, done: Promise<void> }} controller and completion promise
   */
  streamChatMessage(prompt, context = { includeSnowflake: true }, onChunk) {
    const url = this.baseUrl + '/v3/api/bhat/chat?stream=true';
    const controller = new AbortController();

    const payload = {
      prompt,
      context
    };

    const done = (async () => {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/plain, application/x-ndjson, application/json',
          'Authorization': 'Bearer ' + this.authToken
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      if (!response.ok) {
        // Try to read error body for details
        let message = 'Streaming request failed';
        try {
          const text = await response.text();
          message = text || message;
        } catch (e) { /* ignore */ }
        throw new Error(message);
      }

      if (!response.body) {
        throw new Error('Streaming not supported by the browser or no response body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffered = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        buffered += chunk;

        // Handle both raw text and line-delimited formats (SSE/NDJSON)
        let lastNewlineIndex = buffered.lastIndexOf('\n');
        if (lastNewlineIndex !== -1) {
          const lines = buffered.slice(0, lastNewlineIndex + 1).split(/\r?\n/);
          buffered = buffered.slice(lastNewlineIndex + 1);
          for (const line of lines) {
            if (!line) continue;
            // Support SSE-style lines starting with "data:"
            const dataPrefix = 'data:';
            const content = line.startsWith(dataPrefix) ? line.slice(dataPrefix.length).trimStart() : line;
            if (content === '[DONE]') {
              try { controller.abort(); } catch (e) { /* ignore */ }
              return;
            }
            onChunk(content);
          }
        } else {
          // No newline yet; emit as-is for raw text streams
          onChunk(chunk);
        }
      }

      // Flush any remaining buffered text
      if (buffered) {
        onChunk(buffered);
      }
    })();

    return {
      abort: () => {
        try { controller.abort(); } catch (e) { /* ignore */ }
      },
      done
    };
  }

  /**
   * Send the conversation to Notion for the given conversation/session id
   * @param {number|string} conversationId
   * @returns {Promise<any>}
   */
  async sendConversationToNotion(conversationId, payload = {}) {
    if (conversationId === undefined || conversationId === null || conversationId === '') {
      throw new Error('conversationId is required');
    }
    const url = this.baseUrl + `/v3/api/bhat/sessions/${conversationId}/notion`;
    return this.post(url, payload);
  }

  /**
   * List organization prompts (Admin Only)
   * @param {Object} options - Query options (cursor, limit, sort, order, search)
   * @returns {Promise<Object>} Paginated list result with data, count, nextCursor, total
   */
  async getOrganizationPromptsList(options = {}) {
    try {
      let url = this.baseUrl + '/v3/api/ai-insights/organization-prompts/list';
      const queryParams = [];
      
      if (options.cursor) {
        queryParams.push(`cursor=${encodeURIComponent(options.cursor)}`);
      }
      if (options.limit) {
        queryParams.push(`limit=${parseInt(options.limit)}`);
      }
      if (options.sort) {
        queryParams.push(`sort=${encodeURIComponent(options.sort)}`);
      }
      if (options.order) {
        queryParams.push(`order=${encodeURIComponent(options.order)}`);
      }
      if (options.search) {
        queryParams.push(`search=${encodeURIComponent(options.search)}`);
      }
      
      if (queryParams.length > 0) {
        url += '?' + queryParams.join('&');
      }
      
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching organization prompts list:', error);
      throw error;
    }
  }

  /**
   * Get customer IDs that have organization prompts (Admin Only)
   * @returns {Promise<Array<number>>} Array of distinct customer IDs
   */
  async getCustomerIdsWithOrganizationPrompts() {
    try {
      const url = this.baseUrl + '/v3/api/ai-insights/organization-prompts/customer-ids';
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching customer IDs with organization prompts:', error);
      throw error;
    }
  }

  /**
   * Get version history of organization prompts for a customer (Admin Only)
   * @param {number} customerId - The customer ID
   * @returns {Promise<Array>} Array of organization prompt versions
   */
  async getOrganizationPromptHistory(customerId) {
    try {
      const url = this.baseUrl + `/v3/api/ai-insights/organization-prompts/${customerId}/history`;
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching organization prompt history:', error);
      throw error;
    }
  }

  /**
   * Get organization prompt by ID (Admin Only)
   * @param {number} promptId - The prompt ID
   * @returns {Promise<Object>} Organization prompt with content
   */
  async getOrganizationPromptById(promptId) {
    try {
      const url = this.baseUrl + `/v3/api/ai-insights/organization-prompts/${promptId}`;
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching organization prompt by ID:', error);
      throw error;
    }
  }

  /**
   * Create a new version of an organization prompt (Admin Only)
   * @param {number} customerId - The customer ID
   * @param {string} promptContent - The prompt content
   * @returns {Promise<Object>} The newly created organization prompt
   */
  async createOrganizationPrompt(customerId, promptContent) {
    try {
      const url = this.baseUrl + '/v3/api/ai-insights/organization-prompts';
      const payload = {
        customerId,
        promptContent
      };
      const response = await this.post(url, payload);
      return response.data || response;
    } catch (error) {
      console.error('Error creating organization prompt:', error);
      throw error;
    }
  }

  /**
   * Restore a previous version of an organization prompt (Admin Only)
   * @param {number} promptId - The prompt ID to restore
   * @returns {Promise<Object>} The restored organization prompt
   */
  async restoreOrganizationPrompt(promptId) {
    try {
      const url = this.baseUrl + `/v3/api/ai-insights/organization-prompts/${promptId}/restore`;
      const response = await this.put(url, {});
      return response.data || response;
    } catch (error) {
      console.error('Error restoring organization prompt:', error);
      throw error;
    }
  }

  /**
   * Soft delete a version of an organization prompt (Admin Only)
   * @param {number} promptId - The prompt ID to delete
   * @returns {Promise<void>}
   */
  async deleteOrganizationPrompt(promptId) {
    try {
      const url = this.baseUrl + `/v3/api/ai-insights/organization-prompts/${promptId}`;
      await this.delete(promptId, url);
    } catch (error) {
      console.error('Error deleting organization prompt:', error);
      throw error;
    }
  }

  /**
   * Get list of available prompt fragments (All Users)
   * @returns {Promise<Object>} Record of prompt fragments grouped by type
   */
  async getPromptFragmentsList() {
    try {
      const url = this.baseUrl + '/v3/api/ai-insights/prompt-fragments';
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching prompt fragments list:', error);
      throw error;
    }
  }

  /**
   * Get prompt fragment by ID
   * Fetches the list and finds the fragment by ID
   * @param {number} fragmentId - The fragment ID
   * @returns {Promise<Object|null>} Prompt fragment or null if not found
   */
  async getPromptFragmentById(fragmentId) {
    try {
      const fragmentsByType = await this.getPromptFragmentsList();
      // Search through all types to find the fragment
      for (const type in fragmentsByType) {
        const fragment = fragmentsByType[type].find(f => f.promptId === fragmentId);
        if (fragment) {
          return fragment;
        }
      }
      return null;
    } catch (error) {
      console.error('Error fetching prompt fragment by ID:', error);
      throw error;
    }
  }

  /**
   * Get all available recipes
   * @returns {Promise<Array>} List of recipes
   */
  async getRecipes() {
    try {
      const url = this.baseUrl + '/v3/api/bhat/recipes';
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching recipes:', error);
      throw error;
    }
  }

  /**
   * Get single recipe by ID
   * @param {string} recipeId - The recipe ID
   * @returns {Promise<Object>} Recipe details
   */
  async getRecipe(recipeId) {
    try {
      const url = this.baseUrl + `/v3/api/bhat/recipes/${recipeId}`;
      const response = await this.executeGet(url);
      return response.data || response;
    } catch (error) {
      console.error('Error fetching recipe:', error);
      throw error;
    }
  }

  /**
   * Execute a recipe with parameters
   * @param {string} recipeId - Recipe ID
   * @param {Object} params - Recipe parameters
   * @param {number} conversationId - Optional conversation ID to continue
   * @returns {Promise<Object>} Chat response
   */
  async executeRecipe(recipeId, params, conversationId = undefined) {
    try {
      const url = this.baseUrl + '/v3/api/bhat/recipes/execute';
      const payload = {
        recipeId,
        params,
        ...(conversationId !== undefined && conversationId !== null ? { conversationId } : {}),
      };
      const response = await this.post(url, payload);
      return response.data || response;
    } catch (error) {
      console.error('Error executing recipe:', error);
      throw error;
    }
  }
}

export default InsightsService; 